import { storefrontServingState } from "@loot-os/api-contract";
import { Outlet } from "@tanstack/react-router";

import { ErrorAlert } from "../components/ErrorAlert";
import { useStorefrontConfigQuery } from "../lib/storefront";
import { useStandingNotice } from "../lib/useStandingNotice";

/**
 * The route under every storefront section: General at /storefront and one page per section
 * beneath it, listed as the Storefront item's children in `TENANT_NAV`. It loads the config
 * once, so a section renders only once there is one, and keeps the disabled-store notice up on
 * every section rather than on the page that holds the switch.
 *
 * The Outlet is keyed on the environment: a section's draft holds one environment's values,
 * so a switch remounts it rather than carrying the draft into the other environment's form.
 * (A switch also resets the query cache, which unmounts the section through the gate below;
 * the key is the stated contract, not a side effect of the cache.)
 */
export function StorefrontLayout() {
  const { environmentId, query } = useStorefrontConfigQuery();
  const state = query.data ? storefrontServingState(query.data.config) : null;
  useStandingNotice(state === "off", {
    id: "storefront-disabled",
    message: "The storefront is disabled — its domains serve nothing until you enable it.",
  });
  useStandingNotice(state === "coming_soon", {
    id: "storefront-coming-soon",
    message:
      "The storefront is coming soon — its domains show the coming soon page until you set it live.",
  });

  if (query.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading storefront…</div>;
  }
  if (query.error) return <ErrorAlert message={query.error.message} />;
  if (!query.data) return null;

  return <Outlet key={environmentId ?? "live"} />;
}
